import HLSPBSPlayer from '../../../shared/components/VideoPlayer/PlayerEngines/HlsPlayer'
import ShakaPlayer from '../../../shared/components/VideoPlayer/PlayerEngines/ShakaPlayer'
import ShakaPBSPlayer from '../../../shared/components/VideoPlayer/PlayerEngines/ShakaPlayer'
import configuration from '../configuration'
import googleAnalytics4 from '../lib/googleAnalytics'
import { PLATFORMS } from '.'
import dayjs from 'dayjs'
import { getMessage } from '../actions/contentActions'
import { triggerSentryError, ComcastErrorType } from '../../../shared/helpers'
import { checkEnvironmentFlags, validateResponseObject } from '../lib/helpers'

/**
 * Platform base class and default platform for browsers.
 * Each platform supported should extend this class.
 * This class should contain all possible methods, even with empty implementations, 
    this will allow subclasses to only implement necessary methods.
 */
export default class Platform {
  _playerInstance = null
  constructor() {
    if (typeof Platform.instance === 'object') {
      return Platform.instance
    }

    this._mediaEventsDetails = {
      eventCalled: false,
      extraParams: {},
      mediaStartTime: null,
    }

    this._userSawUrgentMessage = false
    this._urgentMessage = {
      information: {},
      validation: {
        isValid: false,
        invalidFields: [],
      },
    }

    Platform.instance = this
    return this
  }

  get playerInstance() {
    return this._playerInstance
  }

  set playerInstance(player) {
    if (this._playerInstance) this._playerInstance.destroy()
    this._playerInstance = player
  }

  set userSawUrgentMessage(booleanFlag) {
    this._userSawUrgentMessage = booleanFlag
  }

  get userSawUrgentMessage() {
    return this._userSawUrgentMessage
  }

  set urgentMessage(data) {
    this._urgentMessage = data
  }

  get urgentMessage() {
    return this._urgentMessage
  }

  get deviceType() {
    return 'samsungtv'
  }

  signIn() {
    // No default behavior
  }

  get deviceModel() {
    return 'browser'
  }

  startPlayback({ sendMediaEvents = false, extraParams = {} } = {}) {
    if (sendMediaEvents) {
      this._configureGoogleAnalyticsMediaEvents(extraParams)
      this._sendStartMediaEvent()
    }
  }

  _configureGoogleAnalyticsMediaEvents(extraParams) {
    this._mediaEventsDetails.extraParams = {
      ...extraParams,
    }
  }

  createPlayer(player, ...args) {
    const playerInstance = new player(...args)
    this.playerInstance = playerInstance
    return playerInstance
  }

  createVODPlayer(playerRef, videoPlayerProps) {
    return this.createPlayer(HLSPBSPlayer, playerRef, videoPlayerProps)
  }

  createLivePlayer(playerRef, videoPlayerProps) {
    return this.createPlayer(HLSPBSPlayer, playerRef, videoPlayerProps)
  }

  createVODDRMPlayer(playerRef, videoPlayerProps) {
    return this.createPlayer(ShakaPlayer, playerRef, videoPlayerProps)
  }

  createLiveDRMPlayer(playerRef, videoPlayerProps) {
    return this.createPlayer(ShakaPBSPlayer, playerRef, videoPlayerProps)
  }

  _sendStartMediaEvent() {
    this._mediaEventsDetails.mediaStartTime = dayjs()
    this._mediaEventsDetails.eventCalled = true

    googleAnalytics4.sendMediaEvent(
      googleAnalytics4.mediaStartEventName,
      {
        ...this._mediaEventsDetails.extraParams,
      },
      'Live Playback Screen'
    )
  }

  _sendStopMediaEvent() {
    const videoStreamingDuration = dayjs().diff(
      this._mediaEventsDetails.mediaStartTime,
      'second'
    )

    googleAnalytics4.sendMediaEvent(googleAnalytics4.mediaStopEventName, {
      ...this._mediaEventsDetails.extraParams,
      ...((videoStreamingDuration || undefined) && {
        video_streaming_duration: videoStreamingDuration,
      }),
    })
  }

  endPlayback() {
    if (this._mediaEventsDetails.eventCalled) {
      this._mediaEventsDetails.eventCalled = false
      this._sendStopMediaEvent()
    }
  }

  getMeasurementId() {
    return configuration.analytics4.samsung.measurementId
  }

  getPlatformName() {
    return PLATFORMS.SAMSUNG
  }

  getLiveStreamAnalyticsParams() {
    return {
      video_player: configuration.analytics4.samsung.livestream_video_player,
      video_title: configuration.analytics4.samsung.livestream_video_title,
    }
  }

  getPlatformType() {
    return
  }

  async getMessageInformation() {
    if (!this.userSawUrgentMessage) {
      try {
        this.urgentMessage.details = await getMessage()
        this.urgentMessage.validation = validateResponseObject(
          this.urgentMessage.details
        )
      } catch (error) {
        triggerSentryError(
          `'Platform.js ${error.name}`,
          {
            errorMessage: error.message ?? 'Undefined error message (default)',
            errorLocation: 'Platform getMessageInformation',
            errorType: ComcastErrorType.other,
          },
          true
        )
      }
    }
  }

  getMessageValidations() {
    return this.urgentMessage.validation
  }

  isMessageAvailableForEnv() {
    return checkEnvironmentFlags(this.urgentMessage.details)
  }

  reportAppReady() {
    // No default behavior
  }
}
